"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from "react";
import { Pause, Play, Smartphone } from "lucide-react";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";
import PhotowallDemoPhone, { type DemoPhoneStage } from "./PhotowallDemoPhone";
import PhotowallDemoWall, { DEFAULT_DEMO_WALL_SETTINGS } from "./PhotowallDemoWall";
import PhotowallTvFrame from "./PhotowallTvFrame";
import { usePhotowallDemoFeed } from "./usePhotowallDemoFeed";
export type DemoSceneTone = "paper" | "night" | "light";
interface SceneBeat {
  chapter: number;
  phone: DemoPhoneStage | null;
  duration: number;
  uploads?: {
    count: number;
    spreadMs: number;
  };
  isCrowd?: boolean;
}
const CHAPTER_KEYS = ["wall", "scan", "arrive", "burst", "settle"] as const;
const CROWD_UPLOADS = 14;
const BEATS: SceneBeat[] = [{
  chapter: 0,
  phone: null,
  duration: 3200
}, {
  chapter: 1,
  phone: "scan",
  duration: 2800
}, {
  chapter: 1,
  phone: "gallery",
  duration: 1400
}, {
  chapter: 1,
  phone: "picking",
  duration: 2400
}, {
  chapter: 1,
  phone: "uploading",
  duration: 2700
},
// Photos reach the wall once the server has saved them.
{
  chapter: 2,
  phone: "done",
  duration: 2400,
  uploads: {
    count: 3,
    spreadMs: 2200
  }
}, {
  chapter: 2,
  phone: "after",
  duration: 4200
}, {
  chapter: 3,
  phone: null,
  duration: 10000,
  uploads: {
    count: CROWD_UPLOADS,
    spreadMs: 6500
  },
  isCrowd: true
}, {
  chapter: 4,
  phone: null,
  duration: 5000
}];
const CHAPTER_DURATIONS = CHAPTER_KEYS.map((_, chapter) => BEATS.filter(beat => beat.chapter === chapter).reduce((sum, beat) => sum + beat.duration, 0));
const TONE_CLASSES: Record<DemoSceneTone, {
  stage: string;
  caption: string;
  track: string;
  fill: string;
  button: string;
}> = {
  paper: {
    stage: "bg-[radial-gradient(ellipse_at_50%_20%,#fbf5ea_0%,#efe3d1_60%,#e2d1ba_100%)]",
    caption: "text-[#3b2f25]",
    track: "bg-[#3b2f25]/12",
    fill: "bg-[#E3971D]",
    button: "bg-[#3b2f25] text-[#fbf5ea] hover:bg-[#2a211a]"
  },
  night: {
    stage: "bg-[radial-gradient(ellipse_at_50%_0%,#3a2a1d_0%,#1b1410_55%,#0e0a08_100%)]",
    caption: "text-[#f6ead8]",
    track: "bg-white/15",
    fill: "bg-[#F9B144]",
    button: "bg-[#F9B144] text-[#2f2114] hover:bg-[#ffc265]"
  },
  light: {
    stage: "bg-[linear-gradient(180deg,#f6f3ef_0%,#ece6de_100%)]",
    caption: "text-stone-900",
    track: "bg-stone-900/10",
    fill: "bg-[#E3971D]",
    button: "bg-stone-900 text-white hover:bg-stone-800"
  }
};
interface PhotowallDemoSceneProps {
  tone?: DemoSceneTone;
  className?: string;
}

/** A looping, captioned walkthrough: scan, upload, and the photo lands on the wall. */
export default function PhotowallDemoScene(t0) {
  const $ = _c(100);
  const {
    tone: t1,
    className
  } = t0;
  const tone = t1 === undefined ? "paper" : t1;
  const t = useTranslations("PhotowallPage.scene");
  const sceneRef = useRef(null);
  const feed = usePhotowallDemoFeed();
  const {
    addUploads
  } = feed;
  const [beatIndex, setBeatIndex] = useState(0);
  const [loop, setLoop] = useState(0);
  const [isUserPaused, setIsUserPaused] = useState(false);
  const [isOnScreen, setIsOnScreen] = useState(false);
  const remainingRef = useRef(BEATS[0].duration);
  const isRunning = isOnScreen && !isUserPaused;
  const beat = BEATS[beatIndex];
  const toneClasses = TONE_CLASSES[tone];
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        setIsUserPaused(true);
      }
      const element = sceneRef.current;
      if (!element) {
        return;
      }
      const observer = new IntersectionObserver(t4 => {
        const [entry] = t4;
        return setIsOnScreen(entry.isIntersecting);
      }, {
        threshold: 0.35
      });
      observer.observe(element);
      return () => observer.disconnect();
    };
    t3 = [];
    $[0] = t2;
    $[1] = t3;
  } else {
    t2 = $[0];
    t3 = $[1];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[2] !== beatIndex || $[3] !== isRunning) {
    t4 = () => {
      if (!isRunning) {
        return;
      }
      const startedAt = performance.now();
      let hasFired = false;
      const timer = setTimeout(() => {
        hasFired = true;
        const nextIndex = (beatIndex + 1) % BEATS.length;
        remainingRef.current = BEATS[nextIndex].duration;
        if (nextIndex === 0) {
          setLoop(_temp);
        }
        setBeatIndex(nextIndex);
      }, remainingRef.current);
      return () => {
        clearTimeout(timer);
        if (!hasFired) {
          remainingRef.current = Math.max(0, remainingRef.current - (performance.now() - startedAt));
        }
      };
    };
    t5 = [isRunning, beatIndex];
    $[2] = beatIndex;
    $[3] = isRunning;
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  useEffect(t4, t5);
  let t6;
  if ($[6] !== addUploads || $[7] !== beatIndex) {
    t6 = () => {
      const uploads = BEATS[beatIndex].uploads;
      if (uploads) {
        addUploads(uploads.count, uploads.spreadMs);
      }
    };
    $[6] = addUploads;
    $[7] = beatIndex;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== addUploads || $[10] !== beatIndex || $[11] !== loop) {
    t7 = [beatIndex, loop, addUploads];
    $[9] = addUploads;
    $[10] = beatIndex;
    $[11] = loop;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  useEffect(t6, t7);
  let t8;
  if ($[13] !== className) {
    t8 = cn("w-full", className);
    $[13] = className;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== toneClasses.stage) {
    t9 = cn("relative overflow-hidden rounded-[clamp(16px,2.4vw,32px)] px-[4%] pb-[5%] pt-[6%]", toneClasses.stage);
    $[15] = toneClasses.stage;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const t10 = feed.images;
  const t11 = !isRunning;
  let t12;
  if ($[17] !== t) {
    t12 = t("wallLabel");
    $[17] = t;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== feed.images || $[20] !== t11 || $[21] !== t12) {
    t13 = <PhotowallTvFrame><PhotowallDemoWall images={t10} settings={DEFAULT_DEMO_WALL_SETTINGS} isPaused={t11} label={t12} /></PhotowallTvFrame>;
    $[19] = feed.images;
    $[20] = t11;
    $[21] = t12;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  const t14 = beat.isCrowd ? "translate-y-0 opacity-100" : "-translate-y-2 opacity-0";
  let t15;
  if ($[23] !== t14) {
    t15 = cn("absolute left-[3%] top-[5%] z-[70] flex items-center gap-2 rounded-full bg-black/60 px-[1.4em] py-[0.6em] text-[clamp(10px,1.2vw,15px)] font-medium text-white backdrop-blur transition-all duration-500 ease-out", t14);
    $[23] = t14;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <span className="flex -space-x-1.5">{[0, 1, 2].map(_temp2)}</span>;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  let t17;
  if ($[26] !== t) {
    t17 = t("crowd", {
      count: CROWD_UPLOADS
    });
    $[26] = t;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  let t18;
  if ($[28] !== t15 || $[29] !== t17) {
    t18 = <div aria-hidden="true" className={t15}>{t16}{t17}</div>;
    $[28] = t15;
    $[29] = t17;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  let t19;
  if ($[31] !== t13 || $[32] !== t18) {
    t19 = <div className="relative ml-auto w-[76%] sm:w-[80%]">{t13}{t18}</div>;
    $[31] = t13;
    $[32] = t18;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  const t20 = beat.phone ? "translate-y-0 rotate-[-4deg] opacity-100" : "translate-y-[18%] rotate-[-8deg] opacity-0";
  let t21;
  if ($[34] !== t20) {
    t21 = cn("absolute bottom-[4%] left-[3%] w-[26%] transition-all sm:w-[21%] duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]", t20);
    $[34] = t20;
    $[35] = t21;
  } else {
    t21 = $[35];
  }
  const t22 = beat.phone ?? "after";
  let t23;
  if ($[36] !== t) {
    t23 = t("eventName");
    $[36] = t;
    $[37] = t23;
  } else {
    t23 = $[37];
  }
  let t24;
  if ($[38] !== t) {
    t24 = t("picker.cancel");
    $[38] = t;
    $[39] = t24;
  } else {
    t24 = $[39];
  }
  let t25;
  if ($[40] !== t) {
    t25 = t("picker.title");
    $[40] = t;
    $[41] = t25;
  } else {
    t25 = $[41];
  }
  let t26;
  if ($[42] !== t) {
    t26 = t("picker.add");
    $[42] = t;
    $[43] = t26;
  } else {
    t26 = $[43];
  }
  let t27;
  if ($[44] !== t24 || $[45] !== t25 || $[46] !== t26) {
    t27 = {
      cancel: t24,
      title: t25,
      add: t26
    };
    $[44] = t24;
    $[45] = t25;
    $[46] = t26;
    $[47] = t27;
  } else {
    t27 = $[47];
  }
  const t28 = 824 + feed.uploadCount;
  let t29;
  if ($[48] !== t22 || $[49] !== t23 || $[50] !== t27 || $[51] !== t28) {
    t29 = <PhotowallDemoPhone stage={t22} eventName={t23} pickerLabels={t27} memoryCount={t28} />;
    $[48] = t22;
    $[49] = t23;
    $[50] = t27;
    $[51] = t28;
    $[52] = t29;
  } else {
    t29 = $[52];
  }
  let t30;
  if ($[53] !== t21 || $[54] !== t29) {
    t30 = <div className={t21}>{t29}</div>;
    $[53] = t21;
    $[54] = t29;
    $[55] = t30;
  } else {
    t30 = $[55];
  }
  let t31;
  if ($[56] !== t19 || $[57] !== t30 || $[58] !== t9) {
    t31 = <div className={t9}>{t19}{t30}</div>;
    $[56] = t19;
    $[57] = t30;
    $[58] = t9;
    $[59] = t31;
  } else {
    t31 = $[59];
  }
  let t32;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = () => setIsUserPaused(_temp3);
    $[60] = t32;
  } else {
    t32 = $[60];
  }
  let t33;
  if ($[61] !== isUserPaused || $[62] !== t) {
    t33 = isUserPaused ? t("play") : t("pause");
    $[61] = isUserPaused;
    $[62] = t;
    $[63] = t33;
  } else {
    t33 = $[63];
  }
  let t34;
  if ($[64] !== toneClasses.button) {
    t34 = cn("grid size-11 shrink-0 place-items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E3971D] focus-visible:ring-offset-2", toneClasses.button);
    $[64] = toneClasses.button;
    $[65] = t34;
  } else {
    t34 = $[65];
  }
  let t35;
  if ($[66] !== isUserPaused) {
    t35 = isUserPaused ? <Play className="size-4 fill-current" /> : <Pause className="size-4 fill-current" />;
    $[66] = isUserPaused;
    $[67] = t35;
  } else {
    t35 = $[67];
  }
  let t36;
  if ($[68] !== t33 || $[69] !== t34 || $[70] !== t35) {
    t36 = <button type="button" onClick={t32} aria-label={t33} className={t34}>{t35}</button>;
    $[68] = t33;
    $[69] = t34;
    $[70] = t35;
    $[71] = t36;
  } else {
    t36 = $[71];
  }
  const t37 = isRunning ? "off" : "polite";
  let t38;
  if ($[72] !== toneClasses.caption) {
    t38 = cn("min-h-[2.6em] text-[clamp(15px,1.6vw,19px)] font-medium leading-snug", toneClasses.caption);
    $[72] = toneClasses.caption;
    $[73] = t38;
  } else {
    t38 = $[73];
  }
  const t39 = `chapters.${CHAPTER_KEYS[beat.chapter]}`;
  let t40;
  if ($[74] !== t || $[75] !== t39) {
    t40 = t(t39);
    $[74] = t;
    $[75] = t39;
    $[76] = t40;
  } else {
    t40 = $[76];
  }
  let t41;
  if ($[77] !== t37 || $[78] !== t38 || $[79] !== t40) {
    t41 = <p aria-live={t37} className={t38}>{t40}</p>;
    $[77] = t37;
    $[78] = t38;
    $[79] = t40;
    $[80] = t41;
  } else {
    t41 = $[80];
  }
  let t42;
  if ($[81] !== beat.chapter || $[82] !== isRunning || $[83] !== loop || $[84] !== toneClasses.fill || $[85] !== toneClasses.track) {
    t42 = CHAPTER_DURATIONS.map((duration, chapter) => <div key={chapter} className={cn("h-1 flex-1 overflow-hidden rounded-full", toneClasses.track)}><div key={`${loop}-${chapter === beat.chapter}`} className={cn("h-full rounded-full", toneClasses.fill)} style={chapter < beat.chapter ? {
        width: "100%"
      } : chapter === beat.chapter ? {
        animation: `scene-progress ${duration}ms linear both`,
        animationPlayState: isRunning ? "running" : "paused"
      } : {
        width: 0
      }} /></div>);
    $[81] = beat.chapter;
    $[82] = isRunning;
    $[83] = loop;
    $[84] = toneClasses.fill;
    $[85] = toneClasses.track;
    $[86] = t42;
  } else {
    t42 = $[86];
  }
  let t43;
  if ($[87] !== t42) {
    t43 = <div className="mt-2 flex gap-1.5" aria-hidden="true">{t42}</div>;
    $[87] = t42;
    $[88] = t43;
  } else {
    t43 = $[88];
  }
  let t44;
  if ($[89] !== t41 || $[90] !== t43) {
    t44 = <div className="min-w-0 flex-1">{t41}{t43}</div>;
    $[89] = t41;
    $[90] = t43;
    $[91] = t44;
  } else {
    t44 = $[91];
  }
  let t45;
  if ($[92] !== t36 || $[93] !== t44) {
    t45 = <figcaption className="mt-4 flex items-center gap-4 px-1">{t36}{t44}</figcaption>;
    $[92] = t36;
    $[93] = t44;
    $[94] = t45;
  } else {
    t45 = $[94];
  }
  let t46;
  if ($[95] === Symbol.for("react.memo_cache_sentinel")) {
    t46 = <style>{"@keyframes scene-progress { from { width: 0; } to { width: 100%; } }"}</style>;
    $[95] = t46;
  } else {
    t46 = $[95];
  }
  let t47;
  if ($[96] !== t31 || $[97] !== t45 || $[98] !== t8) {
    t47 = <figure ref={sceneRef} className={t8}>{t31}{t45}{t46}</figure>;
    $[96] = t31;
    $[97] = t45;
    $[98] = t8;
    $[99] = t47;
  } else {
    t47 = $[99];
  }
  return t47;
}
function _temp3(current_0) {
  return !current_0;
}
function _temp2(index) {
  return <Smartphone key={index} className="size-[1.2em] rounded-[3px] bg-black/40" style={{
    rotate: `${(index - 1) * 12}deg`
  }} />;
}
function _temp(current) {
  return current + 1;
}